// App Store 截图分镜数据。
//
// 一张 slide = 一张商店截图，画布 440x956 CSS px，用 3x DPR 导出得到 1320x2868（6.9 寸档）。
// 导出方式见 scripts/export-app-screenshots.sh。
//
// 三条编排规则（照着 Time Atlas 那套拆出来的，2026-09-20）：
//   1. 全套一个底色，首尾两张压深色当书挡 —— 旧版七张七个背景色，看起来像七个不相干的 App。
//   2. 手机出血、倾斜、不完整，UI 才够大；缩略图尺寸下看不清等于没画。
//   3. 关键数字从界面里抠出来做成浮出卡片，放大到两三倍 —— 这是全套最值钱的一招。

export type Locale = 'zh' | 'en'

/** 浮在手机外面的卡片：从界面里抠出来的关键数字，放大后单独摆。 */
export interface FloatCard {
  /** 小标题，比如「地点」「睡眠」 */
  label: string
  /** 主体数字，越短越好 */
  value: string
  /** 数字后面的小单位，可省 */
  unit?: string
  /** 卡片色号，取自 App 里活动类型的配色 */
  tint: 'teal' | 'blue' | 'amber' | 'green' | 'plum' | 'dark'
  /** 相对画布左上角的位置（CSS px） */
  x: number
  y: number
  /** 轻微旋转，单位 deg */
  rotate?: number
}

/** 第 1 张围在手机周围的功能胶囊：讲「这些数据都汇到一起了」。 */
export interface Chip {
  emoji: string
  label: string
  x: number
  y: number
  rotate?: number
}

/** 第 6 张：从 App 里抠出来的回忆卡，叠在地图上按时间依次排开。 */
export interface StackedCard {
  /** 卡片图片路径（相对 public/） */
  image: string
  x: number
  y: number
  width: number
  rotate?: number
}

/** 第 8 张底部那条真实用户评价。原文一个字不改，只用省略号截断。 */
export interface Quote {
  text: string
  /** 统一写 App Store review，不标国别 */
  source: string
}

export interface Slide {
  id: number
  /** 主标题第一行 */
  title: string
  /** 主标题第二行，渲染成品牌色 */
  titleAccent?: string
  /** 标题下面那句说明，一到两行 */
  sub?: string
  /** 深色底（第 1 张和第 8 张） */
  dark?: boolean
  /** 手机截图路径，相对 public/ */
  image: string
  /** 手机横向偏移（CSS px），正数往右 */
  offsetX?: number
  /** 手机纵向起点（CSS px），越大越往下沉，底部出血 */
  offsetY?: number
  /** 手机宽度（CSS px） */
  phoneWidth?: number
  /** 手机旋转角度 */
  rotate?: number
  /** 取景窗往下挪多少（渲染后的 CSS px）—— 截图上半屏是地图或粘顶标题时切掉 */
  cropTop?: number
  cards?: FloatCard[]
  chips?: Chip[]
  stackedCards?: StackedCard[]
  /** 图片铺满画布下半部当背景，不套手机框（第 6 张用） */
  imageBleed?: boolean
  quote?: Quote
  /** 第 8 张底部的用户量，口径以 ASC 为准 */
  footnote?: string
}

const IMG = '/screenshots/2026-09-20'

export const slidesZh: Slide[] = [
  {
    id: 1,
    title: '一个 App',
    titleAccent: '装下你的一整天',
    sub: '走过的路、待过的地方、睡了多久、拍了什么，自动汇到一起。',
    image: `${IMG}/zh-hero.png`,
    phoneWidth: 372,
    offsetX: 34,
    offsetY: 366,
    rotate: -3,
    chips: [
      { emoji: '🗺', label: '轨迹', x: 26, y: 238, rotate: -3 },
      { emoji: '📍', label: '地点', x: 126, y: 232, rotate: 2 },
      { emoji: '🌙', label: '睡眠', x: 226, y: 238, rotate: -2 },
      { emoji: '🏃', label: '运动', x: 326, y: 234, rotate: 3 },
      { emoji: '📷', label: '照片', x: 58, y: 296, rotate: 2 },
      { emoji: '✈️', label: '游记', x: 158, y: 300, rotate: -2 },
      { emoji: '✍️', label: '日记', x: 258, y: 296, rotate: 3 },
    ],
  },
  {
    id: 2,
    title: '你只管走',
    titleAccent: '它一直在记',
    sub: '酒店、博物馆、国家公园、学校，名字和类型都自动填好，不用你动手。',
    image: `${IMG}/zh-place.png`,
    phoneWidth: 366,
    offsetX: 28,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 3,
    title: '出门一趟',
    titleAccent: '回来游记已经写好',
    sub: '行程自动分段，哪天在哪座城市、去了几个地方，全排好了。',
    image: `${IMG}/zh-trips.png`,
    phoneWidth: 366,
    offsetX: 32,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 4,
    title: '几点上班、几点下班',
    titleAccent: '它比你清楚',
    sub: '不用打卡，工作时长和上下班时间自动算出来。',
    image: `${IMG}/zh-work.png`,
    phoneWidth: 366,
    offsetX: 38,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 5,
    title: '去过的城市',
    titleAccent: '一座座点亮',
    sub: '走过的路连成一颗属于你自己的星球。',
    dark: true,
    image: `${IMG}/zh-globe.png`,
    phoneWidth: 392,
    offsetX: 24,
    offsetY: 288,
    rotate: -3,
  },
  {
    id: 6,
    title: '去年的今天',
    titleAccent: '你在哪儿',
    sub: '上个月、半年前、每一个去年的今天，自动翻出来给你看。',
    image: `${IMG}/zh-mapbg.png`,
    imageBleed: true,
    offsetY: 236,
    cropTop: 0,
    stackedCards: [
      { image: `${IMG}/zh-card1.png`, x: 16, y: 292, width: 408, rotate: -1.5 },
      { image: `${IMG}/zh-card2.png`, x: 16, y: 440, width: 408, rotate: 1 },
      { image: `${IMG}/zh-card3.png`, x: 16, y: 588, width: 408, rotate: -1 },
      { image: `${IMG}/zh-card4.png`, x: 16, y: 736, width: 408, rotate: 1.5 },
    ],
  },
  {
    id: 7,
    title: '时间都花在',
    titleAccent: '哪些事上',
    sub: '工作、通勤、睡觉、吃饭、看展、运动，每一类各占多少。',
    image: `${IMG}/zh-types.png`,
    phoneWidth: 366,
    offsetX: 38,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 8,
    title: '只在你手机里',
    titleAccent: '不注册、不上传',
    dark: true,
    image: `${IMG}/zh-settings.png`,
    phoneWidth: 330,
    offsetX: 58,
    offsetY: 352,
    rotate: -3,
    footnote: '已有 1000+ 人在用',
    quote: {
      text: '打开地图全是家到公司两点一线……看着记录才惊觉，好久没在工作日的午后逛过街了',
      source: 'App Store 评论',
    },
  },
]

export const slidesEn: Slide[] = [
  {
    id: 1,
    title: 'Busy every day.',
    titleAccent: 'No idea where it went.',
    dark: true,
    image: `${IMG}/en-timeline.png`,
    phoneWidth: 344,
    offsetX: 48,
    offsetY: 286,
    rotate: -4,
    quote: {
      text: "My weekday map is just home to office and back… seeing it laid out made me realize how long it'd been since I walked around on a weekday afternoon.",
      source: 'App Store review',
    },
  },
  {
    id: 2,
    title: 'You just live.',
    titleAccent: 'Retrace remembers.',
    sub: 'No check-ins, no typing. Just carry your phone.',
    image: `${IMG}/en-map.png`,
    phoneWidth: 366,
    offsetX: 28,
    offsetY: 298,
    rotate: -3,
    cards: [
      { label: 'Places today', value: '6', tint: 'teal', x: 244, y: 344, rotate: 3 },
      { label: 'Walked', value: '30', unit: 'min', tint: 'green', x: 8, y: 470, rotate: -3 },
      { label: 'Distance', value: '25', unit: 'km', tint: 'blue', x: 268, y: 576, rotate: -2 },
    ],
  },
  {
    id: 3,
    title: 'A year later,',
    titleAccent: 'every day is still there.',
    sub: 'Scroll back to any date and see where you were.',
    image: `${IMG}/en-calendar.png`,
    phoneWidth: 366,
    offsetX: 40,
    offsetY: 300,
    rotate: -3,
    cropTop: 320,
  },
  {
    id: 4,
    title: 'Come home to',
    titleAccent: 'a finished travel story.',
    sub: 'Trips are detected and laid out for you — days, places, routes.',
    image: `${IMG}/en-trip.png`,
    phoneWidth: 366,
    offsetX: 32,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 5,
    title: 'See where',
    titleAccent: 'your time actually goes.',
    sub: 'Work, commute, sleep, time spent out — all counted.',
    image: `${IMG}/en-report.png`,
    phoneWidth: 366,
    offsetX: 38,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 6,
    title: 'Every city',
    titleAccent: "you've been, lit up.",
    sub: 'Your own map, drawn by the places you actually went.',
    image: `${IMG}/en-footprint.png`,
    phoneWidth: 366,
    offsetX: 32,
    offsetY: 300,
    rotate: -3,
    cropTop: 96,
  },
  {
    id: 7,
    title: 'Your data stays yours',
    titleAccent: 'and you can take it out.',
    sub: 'On device, backed up to iCloud, exportable — and importable from other trackers.',
    image: `${IMG}/en-settings.png`,
    phoneWidth: 366,
    offsetX: 38,
    offsetY: 300,
    rotate: -3,
  },
  {
    id: 8,
    title: 'Never forget',
    titleAccent: "where you've been.",
    dark: true,
    image: `${IMG}/en-timeline.png`,
    phoneWidth: 330,
    offsetX: 58,
    offsetY: 352,
    rotate: -3,
    footnote: 'Join 1,000+ people',
  },
]

export const slidesByLocale: Record<Locale, Slide[]> = {
  zh: slidesZh,
  en: slidesEn,
}
